import { useState } from 'react';

import ConfirmModal from '@/components/ui/ConfirmModal';
import { updateSiteCmsOfferApi } from '@/lib/api/siteCmsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import type { SiteCmsOwnerType } from '@shared/types/entities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import CmsDomainControl from './CmsDomainControl';

type CmsOfferControlProps = {
  ownerType: SiteCmsOwnerType;
  ownerId: number;
  ownerName: string;
  enabled: boolean;
};

/** Super Admin only. This is an entitlement override, not a Stripe subscription. */
export default function CmsOfferControl({ ownerType, ownerId, ownerName, enabled }: CmsOfferControlProps) {
  const queryClient = useQueryClient();
  const [confirmDisable, setConfirmDisable] = useState(false);
  const mutation = useMutation({
    mutationFn: (next: boolean) => updateSiteCmsOfferApi(ownerType, ownerId, next),
    onSuccess: result => {
      setConfirmDisable(false);
      toast.success(result.enabled ? 'Option Site activée' : 'Option Site désactivée');
      const key = ownerType === 'ORGANIZATION' ? queryKeys.organizations.all() : queryKeys.clubs.all();
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['site-cms', 'access', ownerType, ownerId] });
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Modification impossible'),
  });

  return (
    <div className='rounded-xl border border-slate-200 p-4'>
      <div className='mb-2 flex items-center gap-2'>
        <Globe2 className='h-4 w-4 text-slate-500' />
        <h3 className='text-sm font-bold text-slate-900'>Option Site web personnalisable</h3>
      </div>
      <p className='mb-3 text-xs leading-relaxed text-slate-500'>
        Accès CMS indépendant du plan Pro/Forfait. Attribué uniquement par le Super Admin.
      </p>
      <div className='flex items-center justify-between gap-3'>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}
        >
          {enabled ? 'Activée' : 'Verrouillée'}
        </span>
        <button
          type='button'
          disabled={mutation.isPending}
          onClick={() => (enabled ? setConfirmDisable(true) : mutation.mutate(true))}
          className='inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50'
        >
          {mutation.isPending && <Loader2 className='h-4 w-4 animate-spin' />}
          {enabled ? 'Désactiver' : 'Activer'}
        </button>
      </div>
      <CmsDomainControl ownerType={ownerType} ownerId={ownerId} />
      <ConfirmModal
        isOpen={confirmDisable}
        title='Désactiver l’option Site ?'
        message={`L’accès au CMS de « ${ownerName} » sera retiré sans suppression de ses données. Si un site est actuellement en ligne, ses pages et PDF publics seront révoqués avant validation de la désactivation. Si cette révocation échoue, l'opération sera refusée.`}
        confirmLabel='Désactiver'
        isLoading={mutation.isPending}
        onConfirm={() => mutation.mutate(false)}
        onCancel={() => setConfirmDisable(false)}
      />
    </div>
  );
}
